import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Switch } from '../ui/switch';
import { API, errorText } from '../governance_v2/constants';
import { MODULE_LABELS, VISUAL_LABELS } from './constants';

const SELECT = 'h-9 w-full rounded-md border border-slate-300 bg-transparent px-2 text-sm dark:border-slate-700';

const SettingRow = ({ companyId, item, agents, modules }) => {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (patch) => axios.put(`${API}/companies/${companyId}/iad/settings/${item.code}`, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['iad-settings', companyId] }); qc.invalidateQueries({ queryKey: ['iad-index', companyId] }); toast.success(`${item.code} actualizado`); },
    onError: (e) => toast.error(errorText(e)),
  });
  const module = item.owner_module || item.default_owner_module;
  return (
    <tr className="border-t border-slate-200 dark:border-slate-700" data-testid={`iad-setting-${item.code}`}>
      <td className="p-3"><Switch className="data-[state=unchecked]:bg-slate-400 dark:data-[state=unchecked]:bg-slate-600" checked={item.enabled} onCheckedChange={(v) => save.mutate({ enabled: v })} data-testid={`iad-setting-enabled-${item.code}`} /></td>
      <td className="p-3">
        <p className="font-medium text-slate-900 dark:text-white"><span className="font-mono text-amber-600 dark:text-amber-400">{item.code}</span> · {item.name}</p>
        <p className="text-xs text-slate-500">{item.purpose} · {VISUAL_LABELS[item.visual_type]}</p>
      </td>
      <td className="p-3">
        <select className={SELECT} value={item.owner_module || ''} onChange={(e) => {
          const m = e.target.value || null;
          const agent = agents.find((a) => a.id === item.owner_agent_id);
          const keepAgent = agent && (!(m || item.default_owner_module) || agent.module_type === (m || item.default_owner_module));
          save.mutate(keepAgent ? { owner_module: m } : { owner_module: m, owner_agent_id: null });
        }} data-testid={`iad-setting-module-${item.code}`}>
          <option value="">Por defecto ({MODULE_LABELS[item.default_owner_module] || 'Corporativo'})</option>
          {modules.map((m) => <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>)}
        </select>
      </td>
      <td className="p-3">
        <select className={SELECT} value={item.owner_agent_id || ''} onChange={(e) => save.mutate({ owner_agent_id: e.target.value || null })} data-testid={`iad-setting-agent-${item.code}`}>
          <option value="">Especialista genérico del módulo</option>
          {agents.filter((a) => !module || a.module_type === module).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </td>
    </tr>
  );
};

export const IADCompanySettings = ({ companyId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['iad-settings', companyId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/iad/settings`)).data,
    enabled: !!companyId,
  });
  return (
    <div className="space-y-3" data-testid="iad-company-settings">
      <p className="text-sm text-slate-500">Activa las metodologías que SUPER-100 puede usar para esta empresa y define qué GOV (módulo) y qué agente especialista ejecuta cada una.</p>
      {isLoading && <p className="text-sm text-slate-400">Cargando metodologías…</p>}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50">
            <tr><th className="p-3">Activo</th><th className="p-3">Metodología</th><th className="p-3">GOV / módulo dueño</th><th className="p-3">Agente que ejecuta</th></tr>
          </thead>
          <tbody>{(data?.items || []).map((i) => <SettingRow key={i.code} companyId={companyId} item={i} agents={data.agents} modules={data.modules} />)}</tbody>
        </table>
      </div>
    </div>
  );
};
